import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { qtyDisplay } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy, RefreshCw, Mail, Trash2, DollarSign, Search } from "lucide-react";

// One-tap wording for the note that goes to the customer. The strength one matters:
// North American inhalers are labelled by delivered dose (44/110/220) and Canadian and
// European ones by metered dose (50/125/250), so the same medicine reads as two numbers.
const NOTE_TEMPLATES = [
  {
    label: "Same med, US vs Canada label",
    text: (med, est) => {
      const dispensed = med ? `${med.name} ${med.strength}` : "this medication";
      const us = med?.strength || "";
      const ca = (est?.strength || "").trim() || (est?.requested_strength || "").trim();
      const confirm = " We will know for sure once you upload your prescription.";
      if (us && ca) return `We are dispensing ${dispensed}. This is the same medication — it is labelled ${us} in the United States and ${ca} in Canada.${confirm}`;
      return `We are dispensing ${dispensed}. This is the same medication, labelled one way in the United States and another in Canada.${confirm}`;
    },
  },
  {
    label: "Generic equivalent",
    text: (med) => `We are dispensing ${med ? `${med.name} ${med.strength}` : "a generic equivalent"}, which contains the same active ingredient at the same strength as the brand on your prescription. Your licensed pharmacist confirms the match before it is dispensed.`,
  },
  {
    label: "Pack size note",
    text: (med) => med ? `This is priced as a pack of ${med.pack_size} ${med.pack_unit || "units"}, which is the pack size our pharmacy stocks.` : "This is priced at the pack size our pharmacy stocks.",
  },
  { label: "Clear", text: () => "" },
];

/* Every colour below is an explicit light/dark pair on purpose - this console is audited for
   WCAG contrast in BOTH themes, so a dark-only token (e.g. text-yellow-300 with no light
   counterpart) silently fails the light-mode audit. */
const STATUS_BADGE = {
  new: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300 border-yellow-500/20",
  awaiting_manual_quote: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/20",
  quoted: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
  rx_uploaded: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/20",
  rx_in_review: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/25",
  rx_approved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/25",
  rx_adjusted: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/25",
  rx_rejected: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/25",
  paid: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/20",
  expired: "bg-black/[0.03] dark:bg-white/5 text-paw-text-muted dark:text-white/40 border-paw-border dark:border-white/10",
};

/* Raw status keys are machine names. These are what the pharmacy team reads. */
const STATUS_LABEL = {
  awaiting_manual_quote: "manual quote",
  rx_uploaded: "rx uploaded",
  rx_in_review: "in rx review",
  rx_approved: "rx approved",
  rx_adjusted: "qty adjusted",
  rx_rejected: "rx rejected",
};

export default function AdminEstimates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [selected, setSelected] = useState(null);
  const [manualFor, setManualFor] = useState(null);
  const [manualForm, setManualForm] = useState({ base_cost: "", markup_pct: "30", shipping: "9.99", packs: "1", substitution_note: "" });
  const [medQuery, setMedQuery] = useState("");
  const [medResults, setMedResults] = useState([]);
  const [medLoading, setMedLoading] = useState(false);
  const [pickedMed, setPickedMed] = useState(null);
  const [manualSubmitting, setManualSubmitting] = useState(false);

  // Catalogue search for pricing a custom request off a real pack.
  useEffect(() => {
    if (!manualFor) return;
    const q = medQuery.trim();
    if (q.length < 2) { setMedResults([]); return; }
    let dead = false;
    setMedLoading(true);
    const t = setTimeout(() => {
      api.get(`/admin/medications/lookup?q=${encodeURIComponent(q)}`)
        .then(({ data }) => { if (!dead) setMedResults(data.results || []); })
        .catch(() => { if (!dead) setMedResults([]); })
        .finally(() => { if (!dead) setMedLoading(false); });
    }, 250);
    return () => { dead = true; clearTimeout(t); };
  }, [medQuery, manualFor]);

  const pickMed = (m) => {
    setPickedMed(m);
    setMedResults([]);
    setMedQuery(`${m.name} ${m.strength}`);
  };
  const clearMed = () => { setPickedMed(null); setMedQuery(""); setMedResults([]); };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/estimates");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async (estimateId) => {
    setGenerating(estimateId);
    try {
      const { data } = await api.post(`/admin/estimates/${estimateId}/generate`);
      toast.success(`Quote generated: $${data.quote.total}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(null);
    }
  };

  const PUBLIC_ORIGIN = "https://pawlinx.com";

  const copyLink = (token) => {
    const url = `${PUBLIC_ORIGIN}/estimate/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied: " + url);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this estimate? This cannot be undone.")) return;
    try { await api.delete(`/admin/estimates/${id}`); toast.success("Estimate deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const submitManualQuote = async () => {
    const base_cost = parseFloat(manualForm.base_cost || "0");
    const markup_pct = parseFloat(manualForm.markup_pct || "0");
    const shipping = parseFloat(manualForm.shipping || "0");
    if (!pickedMed && (!base_cost || base_cost <= 0)) { toast.error("Pick a medication from the catalogue, or enter a base cost greater than 0"); return; }
    setManualSubmitting(true);
    try {
      const { data } = await api.post(`/admin/estimates/${manualFor.estimate_id}/manual-quote`, {
        base_cost, markup_pct, shipping,
        pricing_id: pickedMed ? pickedMed.pricing_id : null,
        packs: Math.max(1, parseInt(manualForm.packs || "1", 10) || 1),
        substitution_note: manualForm.substitution_note || "",
      });
      toast.success(`Quote set: $${data.quote.total}. Customer emailed.`);
      setManualFor(null);
      setManualForm({ base_cost: "", markup_pct: "30", shipping: "9.99", packs: "1", substitution_note: "" }); setPickedMed(null); setMedQuery("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setManualSubmitting(false);
    }
  };

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl" data-testid="admin-estimates">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Pipeline</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Estimate Requests</h1>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-sm transition-colors" data-testid="estimates-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-12 text-center">
          <p className="font-manrope text-paw-text-muted dark:text-white/40">No estimate requests yet.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black/[0.02] dark:bg-white/[0.02] border-b border-paw-border dark:border-white/5">
              <tr className="text-left">
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Customer</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Medication</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Pet</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Status</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Quote</th>
                <th className="px-5 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.estimate_id} className="border-b border-paw-border dark:border-white/5 last:border-0 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors" data-testid={`estimate-row-${e.estimate_id}`}>
                  <td className="px-5 py-4">
                    <p className="font-manrope text-sm font-medium">{e.full_name}</p>
                    <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">{e.email}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-manrope text-sm">{e.medication_name}</p>
                    <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">{e.strength} · {e.dosage_form} · {qtyDisplay(e)}</p>
                  </td>
                  <td className="px-5 py-4 font-manrope text-sm">{e.pet_type}{e.pet_name ? ` · ${e.pet_name}` : ""}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-manrope font-medium uppercase tracking-wider ${STATUS_BADGE[e.status] || STATUS_BADGE.expired}`}>{STATUS_LABEL[e.status] || String(e.status || "").replace(/_/g, " ")}</span>
                  </td>
                  <td className="px-5 py-4 font-manrope text-sm">{e.quote != null ? `$${e.quote}` : "n/a"}</td>
                  <td className="px-5 py-4 text-right space-x-2">
                    {e.status === "awaiting_manual_quote" ? (
                      <button onClick={() => { setManualFor(e); setManualForm({ base_cost: "", markup_pct: "30", shipping: "9.99", packs: "1", substitution_note: "" }); setPickedMed(null); setMedQuery(""); setMedResults([]); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 rounded-md text-xs font-manrope font-semibold text-white transition-colors" data-testid={`estimate-manual-${e.estimate_id}`}>
                        <DollarSign className="w-3 h-3" /> Set Price
                      </button>
                    ) : (e.status === "new" || e.status === "expired") ? (
                      <button onClick={() => generate(e.estimate_id)} disabled={generating === e.estimate_id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-paw-accent hover:bg-paw-accent-hover text-white disabled:opacity-60 rounded-md text-xs font-manrope font-semibold transition-colors" data-testid={`estimate-generate-${e.estimate_id}`}>
                        {generating === e.estimate_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        Generate Quote
                      </button>
                    ) : (
                      <>
                        <button onClick={() => setSelected(e)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 rounded-md text-xs font-manrope transition-colors" data-testid={`estimate-view-${e.estimate_id}`}>View</button>
                        <button onClick={() => copyLink(e.link_token)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 rounded-md text-xs font-manrope transition-colors" data-testid={`estimate-copy-${e.estimate_id}`}>
                          <Copy className="w-3 h-3" /> Link
                        </button>
                      </>
                    )}
                    <button onClick={() => remove(e.estimate_id)} className="inline-flex items-center justify-center p-1.5 hover:bg-red-500/10 border border-paw-border dark:border-white/10 rounded-md transition-colors" title="Delete" data-testid={`estimate-delete-${e.estimate_id}`}>
                      <Trash2 className="w-3 h-3 text-red-700 dark:text-red-400" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setSelected(null)} data-testid="estimate-detail-modal">
          <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-outfit font-bold text-xl mb-1">Quote Breakdown</h3>
            <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mb-5">{selected.medication_name} for {selected.full_name}</p>
            {selected.quote_breakdown && (
              <div className="space-y-2 font-manrope text-sm">
                <div className="flex justify-between border-b border-paw-border dark:border-white/5 pb-2"><span className="text-paw-text-secondary dark:text-white/60">Base cost</span><span>${selected.quote_breakdown.base_cost}</span></div>
                <div className="flex justify-between border-b border-paw-border dark:border-white/5 pb-2"><span className="text-paw-text-secondary dark:text-white/60">Markup ({selected.quote_breakdown.markup_pct}%)</span><span>${selected.quote_breakdown.markup_amount}</span></div>
                <div className="flex justify-between border-b border-paw-border dark:border-white/5 pb-2"><span className="text-paw-text-secondary dark:text-white/60">Shipping</span><span>${selected.quote_breakdown.shipping}</span></div>
                <div className="flex justify-between pt-2 text-base"><span className="font-semibold">Total</span><span className="font-bold text-paw-accent">${selected.quote_breakdown.total}</span></div>
                {selected.quote_breakdown.notes && <p className="text-xs text-paw-text-muted dark:text-white/40 pt-3">{selected.quote_breakdown.notes}</p>}
              </div>
            )}
            <div className="flex gap-2 mt-6">
              <button onClick={() => copyLink(selected.link_token)} className="flex-1 flex items-center justify-center gap-2 h-10 bg-paw-accent hover:bg-paw-accent-hover text-white rounded-lg font-manrope text-sm font-semibold transition-colors">
                <Copy className="w-4 h-4" /> Copy 48hr Link
              </button>
              <button onClick={() => setSelected(null)} className="px-4 h-10 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 rounded-lg font-manrope text-sm transition-colors">Close</button>
            </div>
            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-4 flex items-center gap-1.5">
              <Mail className="w-3 h-3" /> Email delivery is mocked. Share this link manually for now.
            </p>
          </div>
        </div>
      )}
      {manualFor && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto" onClick={() => setManualFor(null)} data-testid="manual-quote-modal">
          <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl max-w-2xl w-full p-6 my-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-outfit font-bold text-xl mb-1">Set Manual Price</h3>
            <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mb-4">
              {manualFor.medication_name} · {manualFor.full_name}
            </p>
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg px-3 py-2 mb-5">
              <p className="font-manrope text-xs text-orange-700 dark:text-orange-300">
                Custom medication, so the auto-quote was skipped. Price it from the catalogue where you can — that carries the real cost onto the order so the ledger knows your margin.
              </p>
            </div>

            {/* price from the real catalogue */}
            <div className="mb-5">
              <label className="font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/50 block mb-1.5">Price from catalogue</label>
              <div className="flex items-center gap-2 h-11 px-3.5 rounded-xl bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10">
                <Search className="w-4 h-4 text-paw-text-muted dark:text-white/40 shrink-0" />
                <input
                  value={medQuery}
                  onChange={(e) => setMedQuery(e.target.value)}
                  placeholder="Search your priced medications, e.g. Fluticasone"
                  className="bg-transparent border-none outline-none text-sm font-manrope w-full text-paw-text dark:text-white"
                  data-testid="manual-med-search"
                />
                {medLoading && <Loader2 className="w-4 h-4 animate-spin text-paw-text-muted dark:text-white/40" />}
              </div>

              {medResults.length > 0 && !pickedMed && (
                <div className="mt-2 max-h-52 overflow-y-auto rounded-xl border border-paw-border dark:border-white/10 divide-y divide-paw-border dark:divide-white/5">
                  {medResults.map((m) => (
                    <button key={m.pricing_id} onClick={() => pickMed(m)} data-testid={`manual-med-${m.pricing_id}`}
                      className="w-full text-left px-3.5 py-2.5 hover:bg-black/[0.03] dark:hover:bg-white/5 transition-colors">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-manrope text-sm">{m.name} <span className="text-paw-text-muted dark:text-white/40">{m.strength}</span></span>
                        <span className="font-manrope text-sm font-semibold shrink-0">${Number(m.fixed_price).toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3 mt-0.5">
                        <span className="font-manrope text-[11px] text-paw-text-muted dark:text-white/40">pack of {m.pack_size} {m.pack_unit}</span>
                        <span className="font-manrope text-[11px] text-emerald-700 dark:text-emerald-400 shrink-0">
                          {m.cost_usd != null ? `costs $${m.cost_usd.toFixed(2)} · margin $${m.margin_usd.toFixed(2)}` : "no cost on file"}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {pickedMed && (
                <div className="mt-2 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-3.5 py-3" data-testid="manual-med-picked">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-manrope text-sm font-semibold">{pickedMed.name} {pickedMed.strength}</p>
                      <p className="font-manrope text-[11px] text-paw-text-muted dark:text-white/50 mt-0.5">
                        pack of {pickedMed.pack_size} {pickedMed.pack_unit} · sells ${Number(pickedMed.fixed_price).toFixed(2)}
                        {pickedMed.cost_usd != null ? ` · costs you $${pickedMed.cost_usd.toFixed(2)}` : " · no cost on file"}
                      </p>
                    </div>
                    <button onClick={clearMed} className="font-manrope text-xs text-paw-text-muted dark:text-white/50 hover:text-red-600 shrink-0">Clear</button>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60">Packs</label>
                    <input type="number" min="1" step="1" value={manualForm.packs}
                      onChange={(e) => setManualForm({ ...manualForm, packs: e.target.value })}
                      className="w-20 h-9 px-3 rounded-lg bg-white dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope" data-testid="manual-packs" />
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {!pickedMed && (
                <div>
                  <label className="font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/50 block mb-1.5">Or type a base cost ($)</label>
                  <input type="number" step="0.01" min="0" value={manualForm.base_cost} onChange={(e) => setManualForm({ ...manualForm, base_cost: e.target.value })} className="w-full h-11 px-4 rounded-xl bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-paw-text dark:text-white text-sm font-manrope focus:outline-none focus:border-paw-accent" placeholder="e.g. 45.00" data-testid="manual-base-cost" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {!pickedMed && (
                  <div>
                    <label className="font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/50 block mb-1.5">Markup (%)</label>
                    <input type="number" step="0.1" min="0" value={manualForm.markup_pct} onChange={(e) => setManualForm({ ...manualForm, markup_pct: e.target.value })} className="w-full h-11 px-4 rounded-xl bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-paw-text dark:text-white text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="manual-markup" />
                  </div>
                )}
                <div className={pickedMed ? "col-span-2" : ""}>
                  <label className="font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/50 block mb-1.5">Shipping ($)</label>
                  <input type="number" step="0.01" min="0" value={manualForm.shipping} onChange={(e) => setManualForm({ ...manualForm, shipping: e.target.value })} className="w-full h-11 px-4 rounded-xl bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-paw-text dark:text-white text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="manual-shipping" />
                </div>
              </div>

              <div>
                <label className="font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/50 block mb-1.5">
                  Note to the customer <span className="normal-case tracking-normal text-paw-text-muted dark:text-white/35">(optional, shown on their quote and in the email)</span>
                </label>
                <textarea rows={3} value={manualForm.substitution_note}
                  onChange={(e) => setManualForm({ ...manualForm, substitution_note: e.target.value })}
                  placeholder="e.g. This is the same medicine as the 125 mcg on your prescription."
                  className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-paw-text dark:text-white text-sm font-manrope focus:outline-none focus:border-paw-accent resize-none"
                  data-testid="manual-note" />
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {NOTE_TEMPLATES.map((t) => (
                    <button key={t.label} onClick={() => setManualForm((f) => ({ ...f, substitution_note: t.text(pickedMed, manualFor) }))}
                      className="px-2.5 h-7 rounded-md bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-[11px] transition-colors">
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {(pickedMed || manualForm.base_cost) && (
                <div className="bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 rounded-lg px-4 py-3 font-manrope text-sm">
                  {(() => {
                    const packs = Math.max(1, parseInt(manualForm.packs || "1", 10) || 1);
                    const b = pickedMed ? Number(pickedMed.fixed_price) * packs : parseFloat(manualForm.base_cost || "0");
                    const m = pickedMed ? 0 : parseFloat(manualForm.markup_pct || "0");
                    const s = parseFloat(manualForm.shipping || "0");
                    const ma = b * (m / 100);
                    const total = b + ma + s;
                    const cost = pickedMed && pickedMed.cost_usd != null ? pickedMed.cost_usd * packs : null;
                    return (
                      <div className="space-y-1">
                        {!pickedMed && <div className="flex justify-between text-paw-text-secondary dark:text-white/60"><span>Markup amount</span><span>${ma.toFixed(2)}</span></div>}
                        {cost != null && <div className="flex justify-between text-paw-text-secondary dark:text-white/60"><span>Costs you</span><span>${cost.toFixed(2)}</span></div>}
                        <div className="flex justify-between text-paw-text-secondary dark:text-white/60"><span>Customer sees (med line)</span><span>${(b + ma).toFixed(2)}</span></div>
                        <div className="flex justify-between text-paw-text-secondary dark:text-white/60"><span>Shipping</span><span>${s.toFixed(2)}</span></div>
                        <div className="flex justify-between pt-2 border-t border-paw-border dark:border-white/10 text-base"><span className="text-paw-text dark:text-white font-semibold">Customer total</span><span className="font-bold text-paw-accent">${total.toFixed(2)}</span></div>
                        {cost != null && (
                          <div className="flex justify-between pt-1 text-emerald-700 dark:text-emerald-400"><span>Your margin on the medication</span><span>${(b - cost).toFixed(2)}</span></div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={submitManualQuote} disabled={manualSubmitting} className="flex-1 flex items-center justify-center gap-2 h-11 bg-paw-accent hover:bg-paw-accent-hover text-white disabled:opacity-60 rounded-lg font-manrope text-sm font-semibold transition-colors" data-testid="manual-submit">
                {manualSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><DollarSign className="w-4 h-4" /> Send Quote to Customer</>}
              </button>
              <button onClick={() => setManualFor(null)} className="px-4 h-11 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 rounded-lg font-manrope text-sm transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
